import React, { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import {
  authenticateShopRequestShopperEmailMailbox,
  createShopRequestShopperEmail,
  deleteShopRequestShopperEmail,
  fetchShopRequestShopperEmailMailboxMessages,
  updateShopRequestShopperEmail,
} from 'apis/ShopRequestAPI';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import { getErrorMessageByPriority, getFirstErrorMessage } from 'helpers/emails';

import ManageShopRequestShopperEmailsEntryModal from './ManageShopRequestShopperEmailsEntryModal';
import ManageShopRequestShopperEmailsModalContent from './ManageShopRequestShopperEmailsModalContent';
import { useShopperContactDirectory } from './useShopperContactDirectory';
import { SHOP_REQUEST_TYPE } from '../../constants';
import ShopperEmailsEntryModal from '../../ShopperEmails/ShopperEmailsEntryModal';
import { useShopperEmailsMailboxActions } from '../../ShopperEmails/useShopperEmailsMailboxActions';
import { MysteryShopRequestInbound, ShopperEmailType, ShopRequestShopperEmailType } from '../../types';

type Props = {
  openModal: boolean;
  shopRequest: MysteryShopRequestInbound;
  shopperEmails: ShopRequestShopperEmailType[];
  isFetchingShopperEmails: boolean;
  isLoadingShopperEmails: boolean;
  refetchShopperEmails: () => Promise<any>;
  onClose: () => void;
};

const ManageShopRequestShopperEmailsModal = ({
  openModal,
  shopRequest,
  shopperEmails,
  isFetchingShopperEmails,
  isLoadingShopperEmails,
  refetchShopperEmails,
  onClose,
}: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const shopRequestId = shopRequest?.id;
  const isSpeedToLead = shopRequest?.request_type === SHOP_REQUEST_TYPE.SPEED_TO_LEAD;
  const phoneRequired = isSpeedToLead;
  const [emailToEdit, setEmailToEdit] = useState<ShopRequestShopperEmailType | null>(null);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<ShopRequestShopperEmailType | null>(null);
  const [syncingEmailId, setSyncingEmailId] = useState<number | null>(null);
  const [linkingContactId, setLinkingContactId] = useState<number | null>(null);
  const {
    availableShopperContacts,
    fetchNextPage: fetchNextShopperContactsPage,
    filteredLinkedShopperEmails,
    hasNextPage: hasNextShopperContactsPage,
    isFetchingNextPage: isFetchingNextShopperContactsPage,
    isLoading: isLoadingAccessibleShopperContacts,
    search: contactSearch,
    setSearch: setContactSearch,
  } = useShopperContactDirectory({ enabled: openModal, linkedShopperEmails: shopperEmails });

  const invalidateActivities = useCallback(() => {
    void queryClient.invalidateQueries([
      'shop-request',
      'community',
      'activities',
      shopRequest?.community?.id,
      shopRequestId,
    ]);
  }, [queryClient, shopRequest?.community?.id, shopRequestId]);

  const isRequestShopperEmailMailboxActive = useCallback(
    async (shopperEmailId: number) => {
      const response = await refetchShopperEmails();
      const shopperEmail = response?.data?.find((item: ShopRequestShopperEmailType) => item.id === shopperEmailId);
      return shopperEmail?.mailbox_connection_status === 'active';
    },
    [refetchShopperEmails]
  );

  const { authenticatingEmailId, handleMailboxAction } = useShopperEmailsMailboxActions(refetchShopperEmails, {
    authenticateMailbox: (shopperEmailId) => {
      if (!shopRequestId) {
        throw new Error('Shop request is required for mailbox authentication.');
      }

      return authenticateShopRequestShopperEmailMailbox(shopRequestId, shopperEmailId);
    },
    isMailboxActive: isRequestShopperEmailMailboxActive,
  });

  const { mutateAsync: createShopperEmail, isLoading: isCreating } = useMutation({
    mutationFn: (payload: ShopRequestShopperEmailType) =>
      createShopRequestShopperEmail(shopRequestId as number, payload),
    onSuccess: async () => {
      await refetchShopperEmails();
      invalidateActivities();
      showMessage('success', 'Shopper contact linked successfully.');
    },
  });

  const { mutateAsync: updateShopperEmail, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ShopRequestShopperEmailType }) =>
      updateShopRequestShopperEmail(shopRequestId as number, id, payload),
    onSuccess: async () => {
      await refetchShopperEmails();
      invalidateActivities();
      showMessage('success', 'Shopper email link updated successfully.');
    },
  });

  const { mutateAsync: unlinkShopperEmail, isLoading: isDeleting } = useMutation({
    mutationFn: (shopperEmailId: number) => deleteShopRequestShopperEmail(shopRequestId as number, shopperEmailId),
    onSuccess: async () => {
      await refetchShopperEmails();
      invalidateActivities();
      showMessage('success', 'Shopper email unlinked successfully.');
    },
  });

  const closeEntryModal = useCallback(() => {
    setEmailToEdit(null);
  }, []);

  const handleDelete = async () => {
    if (!emailToDelete?.id) {
      return;
    }

    try {
      await unlinkShopperEmail(emailToDelete.id);
      setEmailToDelete(null);
    } catch (error: any) {
      showMessage('error', getFirstErrorMessage(error) ?? 'Failed to unlink shopper contact.');
    }
  };

  const handleSyncMailbox = useCallback(
    async (shopperEmailId?: number) => {
      if (!shopRequestId || !shopperEmailId || syncingEmailId) {
        return;
      }

      setSyncingEmailId(shopperEmailId);

      try {
        const response = await fetchShopRequestShopperEmailMailboxMessages(shopRequestId, shopperEmailId);
        await refetchShopperEmails();
        await queryClient.invalidateQueries([
          'shop-request',
          'community',
          'activities',
          shopRequest?.community?.id,
          shopRequestId,
        ]);

        if (response?.skipped) {
          showMessage('info', response?.detail || 'No community mailboxes are available for matching yet.');
        } else {
          showMessage('success', response?.detail || 'Mailbox messages fetched successfully.');
        }
      } catch (error: any) {
        showMessage('error', getFirstErrorMessage(error) ?? 'Failed to fetch mailbox messages.');
      } finally {
        setSyncingEmailId(null);
      }
    },
    [queryClient, refetchShopperEmails, shopRequest?.community?.id, shopRequestId, showMessage, syncingEmailId]
  );

  const handleLinkContact = useCallback(
    async (shopperContact: ShopperEmailType, wasJustCreated = false) => {
      if (isCreating || linkingContactId !== null) {
        return;
      }

      setLinkingContactId(shopperContact.id);
      try {
        await createShopperEmail({
          shopper_contact_id: shopperContact.id,
          label: null,
        });
      } catch (error: any) {
        const linkError = getErrorMessageByPriority(error?.response?.data, 'Failed to link shopper contact.', [
          'phone',
          'email',
          'shopper_contact_id',
          'detail',
          'non_field_errors',
        ]);
        showMessage(
          'error',
          wasJustCreated ? `Shopper contact was created, but linking failed: ${linkError}` : linkError
        );
      } finally {
        setLinkingContactId(null);
      }
    },
    [createShopperEmail, isCreating, linkingContactId, showMessage]
  );

  const handleContactCreated = useCallback(
    async (shopperContact: ShopperEmailType) => {
      await handleLinkContact(shopperContact, true);
    },
    [handleLinkContact]
  );

  const handleLabelSubmit = async (values: ShopRequestShopperEmailType) => {
    if (!emailToEdit?.id) {
      return;
    }

    const payload: ShopRequestShopperEmailType = {
      label: values.label?.trim() || null,
    };

    try {
      await updateShopperEmail({ id: emailToEdit.id, payload });
      closeEntryModal();
    } catch (error: any) {
      showMessage(
        'error',
        getErrorMessageByPriority(error?.response?.data, 'Failed to update shopper contact label.', [
          'phone',
          'email',
          'shopper_contact_id',
          'detail',
          'non_field_errors',
        ])
      );
    }
  };

  return (
    <>
      <ManageShopRequestShopperEmailsModalContent
        open={openModal}
        singleUse={isSpeedToLead}
        search={contactSearch}
        linkedContactsTableProps={{
          shopperEmails: filteredLinkedShopperEmails,
          isFetchingShopperEmails,
          isLoadingShopperEmails,
          isDeleting,
          authenticatingEmailId,
          syncingEmailId,
          onEdit: setEmailToEdit,
          onDelete: setEmailToDelete,
          onMailboxAction: handleMailboxAction,
          onSyncMailbox: handleSyncMailbox,
        }}
        availableContactsTableProps={{
          shopperContacts: availableShopperContacts,
          isLoading: isLoadingAccessibleShopperContacts,
          hasNextPage: hasNextShopperContactsPage,
          isFetchingNextPage: isFetchingNextShopperContactsPage,
          onLoadMore: () => fetchNextShopperContactsPage(),
          linkingContactId,
          onLink: handleLinkContact,
        }}
        onAddContact={() => setAddContactModalOpen(true)}
        onClose={onClose}
        onSearchChange={setContactSearch}
      />

      <ManageShopRequestShopperEmailsEntryModal
        open={Boolean(emailToEdit)}
        item={emailToEdit ?? undefined}
        isUpdating={isUpdating}
        onCancel={closeEntryModal}
        onSubmit={handleLabelSubmit}
      />

      <ShopperEmailsEntryModal
        open={addContactModalOpen}
        phoneRequired={phoneRequired}
        singleUse={isSpeedToLead}
        onCancel={() => setAddContactModalOpen(false)}
        onSaved={handleContactCreated}
      />

      <ConfirmModal
        open={Boolean(emailToDelete)}
        title="Unlink Shopper Contact"
        description={
          isSpeedToLead
            ? 'This removes the link, but the shopper contact remains permanently reserved for this Speed to Lead request.'
            : 'This will only remove the link from this shop request.'
        }
        confirmLabel="Unlink"
        onCancel={() => setEmailToDelete(null)}
        onConfirm={handleDelete}
        loading={isDeleting}
      />
    </>
  );
};

export default ManageShopRequestShopperEmailsModal;
